/**
 * The signature component: your page beside what AI agents understand about
 * you, in plain language, linked on hover and on focus.
 * All content here is synthetic and labelled.
 */
import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react'
import { BarChart3, Bell, CalendarClock, LifeBuoy, MapPin, Phone, Users, Waypoints } from 'lucide-react'
import { cn } from '@/utils/cn'
import { PlayPause, useInView, usePrefersReducedMotion } from './media'
import { Button, Proof, Status, type StatusKind } from './primitives'
import './sample.css'

/*
  Synthetic sample: a fictional B2B software company. Nothing here is a real
  business, and the name was invented rather than borrowed.

  It used to be a dental practice. Measured against the CRM, that was modelling
  well under 4% of the audience: healthcare of every kind is 241 companies,
  while software, IT services and internet companies are 1,446, the largest
  single bloc by some distance. The sample now looks like the people who
  actually arrive here.

  The copy is written the way a small software company writes its own site,
  specific and unsold, because generic filler is what makes a mockup read as a
  mockup. The mismatch it carries is a price: the page says $49 and agents are
  being told $39. That is the failure this product exists to catch, and it is
  the one a software company is most likely to have, because a pricing page
  gets edited far more often than the markup underneath it.

  The banner photograph used to be a dental treatment room, left behind when
  the sample stopped being a dental practice. It is now the kind of place a
  multi-site operations team actually schedules, which is what this product
  is for.
*/
export const SAMPLE = {
  url: 'crewline.io',
  name: 'Crewline',
  tagline: 'Shift scheduling for teams that run more than one site.',
  intro:
    'We built this for operations managers who were doing it in a spreadsheet at eleven at night. Eight people, one product, and most of our customers came from someone who already uses it.',
  street: '1120 SE Water Avenue',
  city: 'Portland, OR 97214',
  phone: '(503) 555-0168',
  support: 'Weekdays, 6am to 6pm Pacific',
  /* What the pricing page says, against what agents are being told. */
  pricePage: 'From $49 per seat, per month',
  priceAgents: 'From $39 per seat, per month',
  services: [
    { name: 'Rosters', blurb: 'Build a week across every site, and see who is double booked before you publish.' },
    { name: 'Shift swaps', blurb: 'Staff trade among themselves. You approve, or set rules and stop approving.' },
    { name: 'Coverage alerts', blurb: 'A message when a shift goes uncovered inside your notice window.' },
    { name: 'Hours and costs', blurb: 'What each site is scheduled to spend, before the week starts rather than after.' },
  ],
  /* The one photograph on their site: the banner their homepage opens with,
     with their name, what they do, their price and their call to action over
     it. It lives in its own directory rather than with our grounds, because
     it is a picture on somebody else's page, not scenery on ours. Replace
     that file to change what the sample company looks like. */
  photo: '/design-system/sample/crew-line-bg.jpg',
  /* Three questions a buyer actually asks, answered the way a founder would
     answer them on a call. */
  faq: [
    {
      topic: 'payroll',
      q: 'Does this work with our payroll?',
      a: 'We export to most payroll systems, and we have a direct connection to a few. Tell us which one you run and we will say plainly whether it is one of them.',
    },
    {
      topic: 'contracts',
      q: 'Are we locked into a contract?',
      a: 'No. Monthly plans cancel whenever you want. Annual plans are cheaper and we will refund the unused part if you leave early.',
    },
    {
      topic: 'getting started',
      q: 'How long does it take to set up?',
      a: 'Most teams are running one site in an afternoon. If you have more than ten sites we will do the import for you.',
    },
  ],
}

/*
  The one exchange the homepage opens on: a buyer asks who to use, and the
  answer names you.

  The opening line is a setup rather than a typed question. It was "Who
  should we use for shift scheduling?", and before that "What does Crewline
  do?", which asked a reader to already know who Crewline is before the
  picture meant anything. It now names no company and no category: it
  describes the moment, and the chart underneath is what happens in it.

  No numbers anywhere. The bars are relative lengths and the others are
  unnamed. A recommendation rate here ("named in 7 of 10 answers") would be
  an invented statistic about an outcome, and this product has shipped
  invented figures before.

  Who is being asked is drawn by the component, from `aiAgents`, and is no
  longer passed in here. It was one mark and the words "Who ChatGPT names",
  which named a single vendor on a card about being recommended by all of
  them, and let a caller pass one assistant's logo beside another's name.

  The note says why the answer came out that way, because a picture of you
  winning with no cause attached is a boast. The cause is the product.
*/
export const ASKED = {
  ask: 'When a potential buyer asks about a service or product you offer...',
  you: 'You',
  /* Three others, unnamed, at lengths that read as a spread rather than as
     a measurement. Inventing four rival brands to lose to you is a claim
     about a market, not a picture of an outcome. */
  others: [64, 47, 33],
  note: 'Because AI can read and verify your site.',
}

/* "payroll, contracts, and getting started" built from the questions
   themselves, so the panel cannot end up describing questions the page no
   longer asks. The example used to read "insurance, parking, and first
   visits", which is what this sample asked about when it was a dental
   practice: the helper kept working, the comment did not. */
const listed = (items: string[]) =>
  items.length < 2 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`

/* Their icons, in their order. A product site labels every feature; none of
   these has to be guessed from the picture alone. */
const SERVICE_ICONS = [CalendarClock, Users, Bell, BarChart3]

interface Fact {
  id: string
  label: string
  value: string
  status: StatusKind
  region?: string
  note?: string
  action?: string
}

interface FactGroup {
  title: string
  facts: Fact[]
}

/* What an agent understands, in the reader's words. */
export const FACTS: FactGroup[] = [
  {
    title: 'Your business',
    facts: [
      { id: 'name', label: 'Name', value: SAMPLE.name, status: 'good', region: 'name' },
      { id: 'kind', label: 'What you are', value: 'A software product', status: 'good', region: 'name' },
      { id: 'desc', label: 'What you do', value: SAMPLE.tagline, status: 'good', region: 'tagline' },
    ],
  },
  {
    title: 'How to reach you',
    facts: [
      { id: 'address', label: 'Address', value: `${SAMPLE.street}, ${SAMPLE.city}`, status: 'good', region: 'address' },
      { id: 'phone', label: 'Phone', value: SAMPLE.phone, status: 'good', region: 'phone' },
      { id: 'support', label: 'Support hours', value: SAMPLE.support, status: 'good', region: 'support' },
    ],
  },
  {
    title: 'What you offer',
    facts: [
      { id: 'services', label: 'What it does', value: SAMPLE.services.map((s) => s.name).join(', '), status: 'good', region: 'services' },
      {
        id: 'faq',
        label: 'Common questions',
        value: `${SAMPLE.faq.length} answered: ${listed(SAMPLE.faq.map((f) => f.topic))}`,
        status: 'good',
        region: 'faq',
      },
      {
        id: 'price',
        label: 'Price',
        value: SAMPLE.priceAgents,
        status: 'look',
        region: 'price',
        note: 'Your page says $49. AI agents are being told $39.',
        action: 'Use $49',
      },
      { id: 'reviews', label: 'Reviews', value: 'Your page has none, so none are claimed', status: 'absent' },
    ],
  },
]

const REGION_TAG: Record<string, string> = {
  name: 'Name',
  tagline: 'What you do',
  address: 'Address',
  phone: 'Phone',
  support: 'Support hours',
  price: 'Price',
  services: 'What it does',
  faq: 'Common questions',
}

/*
  What the proof card says is worked out from the facts under it, never typed.
  Every fact is confirmed except the ones needing a person, and an absent fact
  is confirmed too: the page really does not say it, and we really are not
  claiming it.
*/
const ALL_FACTS = FACTS.flatMap((g) => g.facts)
const NEEDS_A_LOOK = ALL_FACTS.filter((f) => f.status === 'look').length
const CONFIRMED_LINE = `${ALL_FACTS.length - NEEDS_A_LOOK} of ${ALL_FACTS.length} details confirmed`

/*
  The showreel: the card walks itself through the comparison, lands on the one
  detail that disagrees, fixes it, and starts again. It is the product, not a
  recording of it.
*/
const REEL: { region: string | null; ms: number; fixed?: boolean }[] = [
  { region: 'name', ms: 1000 },
  { region: 'address', ms: 950 },
  { region: 'phone', ms: 950 },
  { region: 'services', ms: 1100 },
  { region: 'faq', ms: 1100 },
  /*
    The price, not the hours. This said 'hours' from when the sample was a
    dental practice, where the mismatch was an opening time; the mismatch is
    the price now. Nothing has had id 'hours' since, so the reel spent its
    two longest steps, 4.1 seconds and the whole point of the sequence,
    pointing at a region that does not exist: nothing lit up on the page, the
    caption read "Looking at: undefined", and the fix changed no row.
  */
  { region: 'price', ms: 2200 },
  { region: 'price', ms: 1900, fixed: true },
  { region: null, ms: 1600, fixed: true },
]

export function Inspector({ compact, className, showreel }: { compact?: boolean; className?: string; showreel?: boolean }) {
  const [hover, setHover] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [fixed, setFixed] = useState(false)
  const [reelRegion, setReelRegion] = useState<string | null>(null)
  const [step, setStep] = useState(0)
  const [playing, setPlaying] = useState(Boolean(showreel))
  const [wrapRef, inView] = useInView<HTMLDivElement>()
  const reduced = usePrefersReducedMotion()
  const running = Boolean(showreel) && playing && inView && !reduced

  useEffect(() => {
    if (!showreel) return
    if (reduced) {
      /* Show the resolved state rather than the journey. */
      setFixed(true)
      setReelRegion(null)
      return
    }
    if (!running) return
    const s = REEL[step]
    setReelRegion(s.region)
    setFixed(Boolean(s.fixed))
    const t = window.setTimeout(() => setStep((i) => (i + 1) % REEL.length), s.ms)
    return () => window.clearTimeout(t)
  }, [showreel, reduced, running, step])

  /* A person's attention wins. The first touch hands the card over for good. */
  const takeOver = () => {
    if (!playing) return
    setPlaying(false)
    setReelRegion(null)
  }
  const onHover = (region: string | null) => {
    if (region) takeOver()
    setHover(region)
  }

  const active = hover ?? selected ?? reelRegion
  const linked = (region?: string) => Boolean(region && active === region)
  const toggle = (region?: string) => {
    if (!region) return
    takeOver()
    setSelected((s) => (s === region ? null : region))
  }

  const onKey = (e: KeyboardEvent<HTMLElement>, region?: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle(region)
    }
  }

  const card = (
    <div className={cn('ds-inspector', !showreel && className)} data-compact={compact || undefined}>
      <div className="ds-inspector__grid">
        {/* Your page */}
        <div className="ds-inspector__human">
          <div className="ds-inspector__head">
            <div className="ds-inspector__title">What Humans See</div>
          </div>
          <div className="ds-frame ds-inspector__site">
          <div className="ds-frame__chrome">
            <span className="ds-frame__dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span className="ds-frame__url">{SAMPLE.url}</span>
            <span className="ds-frame__caption">sample page</span>
          </div>
          <div className="ds-frame__body ds-frame__body--site" onMouseLeave={() => setHover(null)}>
            {/*
              Their website, not ours: their typeface, their colour, their
              layout, and their own light palette in both themes. The only
              SuperSchema thing on it is the mark we draw when a person points
              at something. Nothing here states a price or shows a review,
              because the panel beside it says the page states neither.
            */}
            <div className="ds-web">
              <header className="ds-web__nav">
                <span className="ds-web__brand">
                  <span className="ds-web__mark" aria-hidden="true">
                    <Waypoints />
                  </span>
                  {/* The name lives in the nav, the way it does on a real
                      site, rather than being repeated as a headline over the
                      banner. It stays a Region because the panel's Name row
                      points at it. */}
                  <Region id="name" active={linked('name')} selected={selected === 'name'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                    <span className="ds-web__wordmark ds-web__serif">{SAMPLE.name}</span>
                  </Region>
                </span>
                <nav className="ds-web__links" aria-hidden="true">
                  <span>Product</span>
                  <span>Pricing</span>
                  <span>Docs</span>
                </nav>
                <span className="ds-web__navcta" aria-hidden="true">
                  Start free
                </span>
              </header>

              {/*
                A banner the way a real product homepage opens: the picture
                behind, the name, what it does, the price and the call to
                action over it. The scrim runs bottom-up under the type,
                because that is the convention on their kind of page, and this
                is a drawing of their page rather than of ours.
              */}
              <div className="ds-web__hero">
                <img
                  className="ds-web__heroimg"
                  src={SAMPLE.photo}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                />
                <div className="ds-web__herotext">
                  <Region id="tagline" active={linked('tagline')} selected={selected === 'tagline'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                    <div className="ds-web__h1 ds-web__serif">{SAMPLE.tagline}</div>
                  </Region>
                  {/* The price sits in the hero because that is where a product
                      site puts it, and because the mismatch has to be somewhere
                      a reader can point at. */}
                  <Region id="price" active={linked('price')} selected={selected === 'price'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                    <p className="ds-web__price">{SAMPLE.pricePage}</p>
                  </Region>
                  <div className="ds-web__actions" aria-hidden="true">
                    <span className="ds-web__btn">Start free</span>
                    <span className="ds-web__btn ds-web__btn--ghost">Book a demo</span>
                  </div>
                </div>
              </div>

              <div className="ds-web__about">
                <p className="ds-web__intro">{SAMPLE.intro}</p>
              </div>

              <Region id="services" active={linked('services')} selected={selected === 'services'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                <div className="ds-web__band">
                  <div className="ds-web__h2 ds-web__serif">What it does</div>
                  <ul className="ds-web__services">
                    {SAMPLE.services.map((service, i) => {
                      const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length]
                      return (
                        <li key={service.name} className="ds-web__service">
                          <Icon aria-hidden="true" />
                          <div>
                            <div className="ds-web__servicename">{service.name}</div>
                            <p className="ds-web__serviceblurb">{service.blurb}</p>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </Region>

              <Region id="faq" active={linked('faq')} selected={selected === 'faq'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                <div className="ds-web__band ds-web__band--plain">
                  <div className="ds-web__h2 ds-web__serif">Questions we get asked</div>
                  <dl className="ds-web__faq">
                    {SAMPLE.faq.map((item) => (
                      <div key={item.q} className="ds-web__qa">
                        <dt className="ds-web__q">{item.q}</dt>
                        <dd className="ds-web__a">{item.a}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Region>

              <div className="ds-web__info">
                <Region id="address" active={linked('address')} selected={selected === 'address'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                  <div className="ds-web__infocell">
                    <MapPin aria-hidden="true" />
                    <div>
                      <div className="ds-web__infolabel">Office</div>
                      <div className="ds-web__infovalue">
                        {SAMPLE.street}
                        <br />
                        {SAMPLE.city}
                      </div>
                    </div>
                  </div>
                </Region>
                <Region id="phone" active={linked('phone')} selected={selected === 'phone'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                  <div className="ds-web__infocell">
                    <Phone aria-hidden="true" />
                    <div>
                      <div className="ds-web__infolabel">Sales</div>
                      <div className="ds-web__infovalue">{SAMPLE.phone}</div>
                    </div>
                  </div>
                </Region>
                <Region id="support" active={linked('support')} selected={selected === 'support'} onHover={onHover} onToggle={toggle} onKey={onKey}>
                  <div className="ds-web__infocell">
                    <LifeBuoy aria-hidden="true" />
                    <div>
                      <div className="ds-web__infolabel">Support</div>
                      <div className="ds-web__infovalue">{SAMPLE.support}</div>
                    </div>
                  </div>
                </Region>
              </div>

              {/* The trial terms are in the footer rather than repeated beside
                  the price, so the page does not say it twice. */}
              <div className="ds-web__foot">Crewline. Free for 14 days, no card needed.</div>
            </div>
          </div>
          </div>
        </div>

        {/* What AI agents understand */}
        <div className="ds-inspector__machine">
          <div className="ds-inspector__head">
            <div className="ds-inspector__title">What AI Understands</div>
            <div style={{ marginTop: 12 }}>
              <Proof
                title={fixed ? 'Everything checks out' : CONFIRMED_LINE}
                detail={fixed ? 'Fixed just now. We check again tonight.' : 'Checked last night. One detail needs a look below.'}
              />
            </div>
          </div>
          <ul className="ds-facts" onMouseLeave={() => setHover(null)}>
            {FACTS.map((g) => (
              <li key={g.title}>
                <div className="ds-facts__group">{g.title}</div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {g.facts.map((f) => {
                    const isFixed = f.id === 'price' && fixed
                    const status: StatusKind = isFixed ? 'good' : f.status
                    return (
                      <li
                        key={f.id}
                        className="ds-fact"
                        role={f.region ? 'button' : undefined}
                        tabIndex={f.region ? 0 : undefined}
                        aria-pressed={f.region ? selected === f.region : undefined}
                        data-linked={linked(f.region) || undefined}
                        onMouseEnter={() => onHover(f.region ?? null)}
                        onFocus={() => onHover(f.region ?? null)}
                        onBlur={() => setHover(null)}
                        onClick={() => toggle(f.region)}
                        onKeyDown={(e) => onKey(e, f.region)}
                      >
                        <span className="ds-fact__label">{f.label}</span>
                        <span className="ds-fact__status">
                          <Status kind={status} size="sm" />
                        </span>
                        <span className={cn('ds-fact__value', f.status === 'absent' && 'ds-fact__value--absent')}>
                          {isFixed ? SAMPLE.pricePage : f.value}
                        </span>
                        {f.note && !isFixed && (
                          <span className="ds-fact__note">
                            <span>{f.note}</span>
                            {f.action && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setFixed(true)
                                }}
                              >
                                {f.action}
                              </Button>
                            )}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )

  if (!showreel) return card

  return (
    <div className={cn('ds-runner', className)} ref={wrapRef}>
      <div className="ds-runner__head">
        <span className="ds-runner__cap">
          {running
            ? reelRegion
              ? `Looking at: ${REGION_TAG[reelRegion]}`
              : 'Everything checks out.'
            : 'Point at anything to see what AI agents make of it.'}
        </span>
        {!reduced && <PlayPause playing={running} onClick={() => (playing ? takeOver() : setPlaying(true))} />}
      </div>
      {card}
    </div>
  )
}

function Region({
  id,
  active,
  selected,
  onHover,
  onToggle,
  onKey,
  children,
}: {
  id: string
  active: boolean
  selected: boolean
  onHover: (id: string | null) => void
  onToggle: (id?: string) => void
  onKey: (e: KeyboardEvent<HTMLElement>, id?: string) => void
  children: ReactNode
}) {
  return (
    <div
      className="ds-region"
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${REGION_TAG[id]} on your page`}
      data-linked={active || undefined}
      onMouseEnter={() => onHover(id)}
      onFocus={() => onHover(id)}
      onBlur={() => onHover(null)}
      onClick={() => onToggle(id)}
      onKeyDown={(e) => onKey(e, id)}
    >
      {children}
    </div>
  )
}
